import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useNoteCategories,
  useMyNoteRequests,
  useNoteRequestList,
  useNoteSyncStatus,
  useCreateNoteRequest,
  useCancelNoteRequest,
  useResolveNoteRequest,
  useUnlinkNoteListing,
  useUploadNoteAttachment,
} from '../hooks/useNoteRequests';
import { formatDateTimeEastern, formatDateEastern, formatEventDateOnly, timeAgo, parseUtcDate } from '../utils/dates';
import { buildCompareUrl } from '../utils/compareLink';
import { isAdminLike, isStaff as isStaffRole } from '../utils/roles';
import NoteRequestApplyModal from '../components/NoteRequestApplyModal';
import NoteRequestLinkPanel from '../components/NoteRequestLinkPanel';
import NoteRequestBulkModal from '../components/NoteRequestBulkModal';
import {
  normalizeOrder,
  orderNumberProblem,
  StatusChip,
  ListingStateChip,
  STATUS_LABELS,
  REVIEW_REASON_LABELS,
  OPEN_STATUSES,
  secRow,
  noteSummary,
  NoteDiff,
  pluralize,
  useDebounced,
} from '../components/NoteRequestBits';
import {
  useAttachmentDraft,
  AttachmentPicker,
  AttachmentStrip,
  AttachmentBadge,
  uploadAll,
} from '../components/NoteAttachments';
import { imageFilesFrom } from '../utils/imagePrep';
import { customTagProblem, mergeTags, tagCountProblem, requestWhat } from '../utils/noteTags';

// /orders — the one page every role shares for public-note requests.
//   buyer  : submit an order number + note type, watch its plain-English status
//   staff  : same form, plus the Queue | Waiting | Done tabs with Apply
//            (preview before → after), Link listing, Resolve, Cancel.
// A buyer's RequestView never carries internal notes / evidence / lastError
// (server strips them) and this page's buyer branch never references those
// fields either, so a leak would need both sides to break.
//
// The sweeper status endpoint is staff-only; buyers get enabled/autoApply from
// their own GET /mine payload for the banner.
//
// Photos: the server only sends a buyer the photos they uploaded themselves,
// so the buyer branch can render request.attachments as-is.

// Default note_sync_config.waiting_expiry_days; only /config (admin) exposes
// the live value, so buyer/staff copy quotes the default.
const WAITING_EXPIRY_DAYS = 14;

const NOTE_TEXT_MIN = 3;
const NOTE_TEXT_MAX = 120;
const COMMENT_MAX = 500;

function NoteRequests({ user, onLogout }) {
  const navigate = useNavigate();
  const isStaff = isStaffRole(user.role);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const notify = (msg, isError) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 6000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); }
  };

  const { data: categoriesData, isLoading: categoriesLoading } = useNoteCategories(false);
  const categories = categoriesData?.categories || [];
  const tagOptions = categoriesData?.tagOptions || [];

  // Staff banner source (staff-only endpoint) vs buyer banner source (GET /mine)
  const { data: syncStatus } = useNoteSyncStatus({ enabled: isStaff });
  // Buyers see their complete history (All, newest first); Open narrows it.
  const [myParams, setMyParams] = useState({ scope: 'all', q: '', limit: 100 });
  const mine = useMyNoteRequests(myParams, { enabled: !isStaff });

  const flags = isStaff
    ? { enabled: syncStatus?.enabled, autoApply: syncStatus?.autoApply }
    : { enabled: mine.data?.enabled, autoApply: mine.data?.autoApply };

  return (
    <div className="app-container">
      <nav className="navbar">
        <div className="navbar-brand">{isStaff ? 'Pricing Portal - Note Requests' : 'Note Requests'}</div>
        <div className="navbar-user">
          <span>{user.displayName} ({user.role})</span>
          {isStaff && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/')}>
              Dashboard
            </button>
          )}
          {isAdminLike(user.role) && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin')}>
              Admin Panel
            </button>
          )}
          {user.role === 'pricer' && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/my-artists')}>
              My Artists
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className={`main-content nr-page ${isStaff ? 'nr-staff' : 'nr-buyer'}`}>
        {flags.enabled === false && (
          <div className="nr-banner nr-banner-off">
            Note sync is off — requests are saved and will be checked when an admin turns it on
          </div>
        )}
        {isStaff && flags.autoApply === false && (
          <div className="nr-banner nr-banner-pilot">
            Pilot mode: matched requests wait for a pricer to press Apply
          </div>
        )}
        {error && <div className="message message-error">{error}</div>}
        {success && <div className="message message-success">{success}</div>}

        <SubmitCard categories={categories} tagOptions={tagOptions} categoriesLoading={categoriesLoading} />

        {isStaff ? (
          <StaffBody user={user} notify={notify} syncStatus={syncStatus} />
        ) : (
          <BuyerBody
            user={user}
            params={myParams}
            setParams={setMyParams}
            query={mine}
            autoApply={flags.autoApply}
            notify={notify}
          />
        )}
      </main>
    </div>
  );
}

// ---- Submit card (all roles) -------------------------------------------------

function successText(req, autoApply) {
  const listings = req.listings || [];
  const where = listings.slice(0, 3).map(secRow).join(', ') + (listings.length > 3 ? ', …' : '');
  const found = `found ${pluralize(listings.length, 'listing')} (${where})`;
  const head = `Request #${req.id} submitted`;
  const what = requestWhat(req);
  if (req.status === 'done') {
    const applied = listings.filter(l => l.state === 'applied').length;
    return applied > 0
      ? `${head} — ${what} added to ${pluralize(applied, 'listing')} (${where}) ✓`
      : `${head} — ${found} — already had the ${what} ✓`;
  }
  if (req.status === 'needs_review') return `${head}${listings.length ? ` — ${found}` : ''} — a pricer will review it`;
  if (listings.length > 0 && autoApply && !req.holdForReview) return `${head} — ${found} — adding the ${what} within a minute`;
  if (listings.length > 0) return `${head} — ${found} — a pricer will review and apply it`;
  return `${head} — no listing in Skybox yet; we'll keep checking for ${WAITING_EXPIRY_DAYS} days`;
}

// "— but 1 photo didn't upload (reason) — add it from the request row"
function photoFailText(r) {
  if (!r.failed) return '';
  return ` — but ${pluralize(r.failed, 'photo')} didn't upload (${r.reason}) — add ${r.failed === 1 ? 'it' : 'them'} from the request row`;
}

function SubmitCard({ categories, tagOptions, categoriesLoading }) {
  const [orderNumber, setOrderNumber] = useState('');
  const [selected, setSelected] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [pickedTags, setPickedTags] = useState([]);
  const [customTag, setCustomTag] = useState('');
  const [comment, setComment] = useState('');
  const [hold, setHold] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [successWarn, setSuccessWarn] = useState(false); // some photos failed
  const [dupeId, setDupeId] = useState(null);            // open request for this order (409)
  const [uploading, setUploading] = useState(false);
  const create = useCreateNoteRequest();
  const upload = useUploadNoteAttachment();
  const photos = useAttachmentDraft();
  const busy = create.isPending || uploading || photos.preparing > 0;

  const norm = normalizeOrder(orderNumber);
  const problem = orderNumber.trim() ? orderNumberProblem(orderNumber) : null;
  const hasOther = selected.includes('other');
  const noteTrim = noteText.trim();
  const noteProblem = !hasOther ? null
    : noteTrim.length < NOTE_TEXT_MIN ? `Public note text needs at least ${NOTE_TEXT_MIN} characters`
    : noteTrim.length > NOTE_TEXT_MAX ? `Public note text must be ${NOTE_TEXT_MAX} characters or fewer`
    : /[\r\n]/.test(noteTrim) ? 'Public note text must be a single line'
    : null;
  const commentProblem = comment.length > COMMENT_MAX ? `Comment must be ${COMMENT_MAX} characters or fewer` : null;
  // Chips the admin removed since they were ticked no longer count
  const picked = pickedTags.filter(t => tagOptions.includes(t));
  const tags = mergeTags(picked, customTag);
  const tagProblem = customTagProblem(customTag) || tagCountProblem(tags);

  const willAdd = useMemo(() => {
    const byKey = new Map(categories.map(c => [c.key, c]));
    return selected
      .map(k => (k === 'other' ? noteTrim : byKey.get(k)?.public_text))
      .filter(Boolean);
  }, [selected, categories, noteTrim]);

  const toggleCat = (key) => setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  const toggleTag = (tag) => setPickedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));

  const reset = () => {
    setOrderNumber(''); setSelected([]); setNoteText(''); setComment('');
    setPickedTags([]); setCustomTag('');
    setHold(false); setDupeId(null); photos.reset();
  };

  const showSuccess = (text, warn) => { setFormSuccess(text); setSuccessWarn(!!warn); };

  // Ctrl+V anywhere in the form adds a screenshot. Spreadsheet copies carry
  // the text plus a picture of the cells, so inside a text box the text wins.
  const handlePaste = (e) => {
    const files = imageFilesFrom(e.clipboardData);
    if (!files.length) return;
    const t = e.target;
    const textBox = t && (t.tagName === 'TEXTAREA' || (t.tagName === 'INPUT' && t.type === 'text'));
    if (textBox && (e.clipboardData.getData('text/plain') || '').trim()) return;
    e.preventDefault();
    if (!create.isPending && !uploading) photos.addFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setDupeId(null);
    if (!orderNumber.trim()) { setFormError('Order number is required'); return; }
    if (problem) { setFormError(problem); return; }
    if (!selected.length && !tags.length) { setFormError('Pick a note type or a tag'); return; }
    if (noteProblem) { setFormError(noteProblem); return; }
    if (tagProblem) { setFormError(tagProblem); return; }
    if (commentProblem) { setFormError(commentProblem); return; }
    if (photos.preparing) { setFormError('Photos are still being prepared — one moment'); return; }

    const body = { orderNumber: orderNumber.trim(), categories: selected };
    if (hasOther) body.noteText = noteTrim;
    if (tags.length) body.tags = tags;
    if (comment.trim()) body.comment = comment.trim();
    if (hold) body.holdForReview = true;
    const items = photos.items;

    let res;
    try {
      res = await create.mutateAsync(body);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.existing) {
        const ex = data.existing;
        setFormError(
          `Order ${orderNumber.trim()} already has an open request (#${ex.id}, ${STATUS_LABELS[ex.status] || ex.status}${ex.createdAt ? `, ${timeAgo(ex.createdAt)}` : ''})`
        );
        if (items.length) setDupeId(ex.id);
      } else {
        setFormError(data?.error || 'Failed to submit request');
      }
      return;
    }

    const { request, autoApply } = res.data;
    let text = successText(request, autoApply);
    let warn = false;
    if (items.length) {
      // The request is kept whatever happens to the photos
      setUploading(true);
      try {
        const r = await uploadAll(upload.mutateAsync, request.id, items);
        if (r.ok) text += ` · ${pluralize(r.ok, 'photo')} attached`;
        text += photoFailText(r);
        warn = r.failed > 0;
      } finally {
        setUploading(false);
      }
    }
    showSuccess(text, warn);
    reset();
  };

  // 409 with photos picked: put them on the order's open request instead
  const addToExisting = async () => {
    const id = dupeId;
    if (!id || !photos.items.length) return;
    setFormError('');
    setFormSuccess('');
    setUploading(true);
    let r;
    try {
      r = await uploadAll(upload.mutateAsync, id, photos.items);
    } finally {
      setUploading(false);
    }
    if (!r.ok) {
      setFormError(r.forbidden
        ? "That order's request belongs to another buyer — ask a pricer"
        : `Couldn't add the photos to #${id} (${r.reason})`);
      if (r.forbidden) setDupeId(null);
      return;
    }
    showSuccess(`${pluralize(r.ok, 'photo')} added to request #${id}${photoFailText(r)}`, r.failed > 0);
    reset();
  };

  const dupePhotos = dupeId && photos.items.length > 0;

  return (
    <div className="card nr-submit-card">
      <div className="card-header">
        <h2 className="card-title">Request a public note</h2>
        <span className="nr-muted" style={{ fontSize: '0.75rem' }}>Order number + a note type or a tag — that's all</span>
      </div>

      {formError && (
        <div className="message message-error">
          {formError}
          {dupePhotos ? (
            <div className="nr-dupe-photos">
              <button type="button" className="btn btn-secondary btn-sm" onClick={addToExisting} disabled={busy}>
                {uploading ? 'Adding…' : `Add the ${photos.items.length === 1 ? 'photo' : `${photos.items.length} photos`} to #${dupeId} instead`}
              </button>
            </div>
          ) : null}
        </div>
      )}
      {formSuccess && (
        <div className={`message ${successWarn ? 'nr-form-warn' : 'message-success'} nr-form-success`}>
          <span>{formSuccess}</span>
          <button type="button" className="nr-dismiss" onClick={() => setFormSuccess('')} aria-label="Dismiss">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} onPaste={handlePaste}>
        <div className="form-group">
          <label className="form-label" htmlFor="nr-order">Order number</label>
          <input
            id="nr-order"
            type="text"
            className="form-input nr-order-input"
            value={orderNumber}
            onChange={(e) => { setOrderNumber(e.target.value); if (formSuccess) setFormSuccess(''); if (dupeId) setDupeId(null); }}
            placeholder="e.g. 19-24114/SCS, BRWV - 5274214 - 65663549 or N4Q-6P62YZ7"
            autoComplete="off"
            autoCapitalize="characters"
            required
            autoFocus
          />
          <div className="nr-hint">
            {orderNumber.trim() ? (
              problem ? <span className="nr-hint-bad">{problem}</span> : <>Will match: <code>{norm}</code></>
            ) : (
              'Any vendor format works — the number from your order confirmation'
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Note type {selected.length > 1 ? `(${selected.length} selected)` : ''}</label>
          {categoriesLoading ? (
            <div className="nr-muted">Loading note types…</div>
          ) : categories.length === 0 ? (
            <div className="nr-muted">No note types are configured — ask an admin.</div>
          ) : (
            <div className="nr-cat-chips">
              {categories.map(c => (
                <label
                  key={c.key}
                  className={`nr-cat-chip ${selected.includes(c.key) ? 'on' : ''}`}
                  title={c.public_text ? `Adds “${c.public_text}” to the listing's public notes` : 'Type the exact wording below'}
                >
                  <input type="checkbox" checked={selected.includes(c.key)} onChange={() => toggleCat(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
          {willAdd.length > 0 && (
            <div className="nr-hint">Will add: {willAdd.map((p, i) => <code key={i} className="nr-phrase">{p}</code>)}</div>
          )}
        </div>

        <fieldset className="form-group nr-fieldset">
          <legend className="form-label">Tags <span className="nr-muted">(optional)</span></legend>
          {tagOptions.length > 0 && (
            <div className="nr-cat-chips">
              {tagOptions.map(t => (
                <label key={t} className={`nr-cat-chip ${pickedTags.includes(t) ? 'on' : ''}`} title={`Adds the Skybox tag ${t} (staff only)`}>
                  <input type="checkbox" checked={pickedTags.includes(t)} onChange={() => toggleTag(t)} />
                  {t}
                </label>
              ))}
            </div>
          )}
          <input
            id="nr-tag-custom"
            type="text"
            className="form-input nr-tag-input"
            value={customTag}
            onChange={(e) => setCustomTag(e.target.value)}
            placeholder="your own tag, no spaces"
            aria-label="Your own tag"
            autoComplete="off"
          />
          {(tagProblem || tags.length > 0) && (
            <div className="nr-hint">
              {tagProblem
                ? <span className="nr-hint-bad">{tagProblem}</span>
                : <>Will tag: {tags.map(t => <code key={t}>{t}</code>)}</>}
            </div>
          )}
        </fieldset>

        {hasOther && (
          <div className="form-group">
            <label className="form-label" htmlFor="nr-note-text">Public note text</label>
            <input
              id="nr-note-text"
              type="text"
              className="form-input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="exact wording, e.g. Aisle seats"
              maxLength={NOTE_TEXT_MAX}
              required
            />
            <div className="nr-hint nr-hint-warn">
              This exact text will be shown to ticket buyers on marketplaces after a pricer reviews it
              <span className="nr-counter">{noteTrim.length}/{NOTE_TEXT_MAX}</span>
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="nr-comment">Comment for the pricer <span className="nr-muted">(optional)</span></label>
          <textarea
            id="nr-comment"
            className="form-input nr-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="anything that helps find it — event, section, seats"
            maxLength={COMMENT_MAX}
            rows={2}
          />
          {comment.length > COMMENT_MAX * 0.8 && <div className="nr-hint"><span className="nr-counter">{comment.length}/{COMMENT_MAX}</span></div>}
        </div>

        <div className="form-group">
          <label className="form-label">Photos <span className="nr-muted">(optional)</span></label>
          <AttachmentPicker draft={photos} disabled={create.isPending || uploading} />
        </div>

        <div className="form-group">
          <label className="nr-hold">
            <input type="checkbox" checked={hold} onChange={(e) => setHold(e.target.checked)} />
            Hold for a pricer to check before the note is added
          </label>
          <div className="nr-hint">
            Use when you're not sure the note is right — it waits in the pricers' queue instead of being added automatically
          </div>
        </div>

        <button type="submit" className="btn btn-primary nr-submit-btn" disabled={busy}>
          {create.isPending ? 'Submitting…'
            : uploading ? 'Uploading photos…'
            : photos.preparing ? 'Preparing photos…'
            : 'Submit request'}
        </button>
      </form>
    </div>
  );
}

// ---- Shared date cells -----------------------------------------------------------

// When a request reached a terminal state: the last note write for done
// requests (falls back to updatedAt when everything was already present),
// the human's timestamp for resolved/cancelled, updatedAt for expired.
function completedAt(req) {
  switch (req?.status) {
    case 'done': {
      const times = (req.listings || []).map(l => l.appliedAt).filter(Boolean).map(t => parseUtcDate(t)).filter(Boolean);
      if (times.length) return new Date(Math.max(...times.map(d => d.getTime()))).toISOString();
      return req.updatedAt || null;
    }
    case 'resolved':
    case 'cancelled':
      return req.resolvedAt || req.updatedAt || null;
    case 'expired':
      return req.updatedAt || null;
    default:
      return null;
  }
}

// Absolute Eastern date/time with the relative age underneath.
function When({ value, emptyText = '—' }) {
  if (!value) return <span className="nr-muted">{emptyText}</span>;
  return (
    <span className="nr-when" title={formatDateTimeEastern(value)}>
      <span className="nr-when-abs">{formatDateTimeEastern(value)} ET</span>
      <span className="nr-when-rel">{timeAgo(value)}</span>
    </span>
  );
}

// ---- Photos on rows ------------------------------------------------------------

// Mirrors the API: the owner or staff may add; the uploader or an admin may
// delete. The server enforces the real rule (403 → error shown).
function canAddPhotos(user, req) {
  return isStaffRole(user.role) || (req.submittedBy != null && req.submittedBy === user.id);
}

function canDeletePhoto(user, att) {
  return isAdminLike(user.role) || (att.uploadedBy != null && att.uploadedBy === user.id);
}

function RequestPhotos({ user, req }) {
  return (
    <AttachmentStrip
      request={req}
      canAdd={canAddPhotos(user, req)}
      canDeleteAtt={(att) => canDeletePhoto(user, att)}
    />
  );
}

// Staff only: the submitter ticked "Hold for a pricer to check"
function HeldChip({ req }) {
  if (!req.holdForReview) return null;
  return (
    <span className="nr-chip nr-held-chip" title="The submitter asked for a pricer to check this before the note is added">
      held for pricer
    </span>
  );
}

// ---- Buyer body ----------------------------------------------------------------

function buyerStatusText(req, autoApply) {
  switch (req.status) {
    case 'waiting': return 'Waiting for the listing to appear in Skybox';
    case 'matched': return autoApply ? `Listing found — adding ${requestWhat(req)}…` : 'Listing found — pricer will apply';
    case 'done': {
      const applied = (req.listings || []).filter(l => l.state === 'applied').length;
      const what = requestWhat(req);
      return applied > 0 ? `${what[0].toUpperCase()}${what.slice(1)} added to ${pluralize(applied, 'listing')} ✓` : 'Listing found — nothing to add ✓';
    }
    case 'needs_review': return 'A pricer is reviewing this';
    case 'resolved': return 'Handled by pricer';
    case 'cancelled': return 'Cancelled';
    case 'expired': return `No listing found in ${WAITING_EXPIRY_DAYS} days — check the order number and resubmit`;
    default: return req.status || '';
  }
}

function BuyerBody({ user, params, setParams, query, autoApply, notify }) {
  const cancel = useCancelNoteRequest();
  const { data, isLoading, error, isFetching } = query;
  const requests = data?.requests || [];
  const total = data?.total ?? requests.length;
  const scope = params.scope || 'all';
  const [q, setQ] = useState(params.q || '');
  const dq = useDebounced(q, 300);
  // Push the debounced search into the query params (resets paging)
  useEffect(() => {
    setParams(p => (p.q === dq ? p : { ...p, q: dq, limit: 100 }));
  }, [dq, setParams]);
  const setScope = (s) => setParams(p => ({ ...p, scope: s, limit: 100 }));
  const showMore = () => setParams(p => ({ ...p, limit: Math.min(2000, (p.limit || 100) + 100) }));

  const doCancel = (req) => {
    if (!window.confirm(`Cancel request #${req.id} for order ${req.orderNumber}?`)) return;
    cancel.mutate(req.id, {
      onSuccess: () => notify(`Request #${req.id} cancelled`),
      onError: (err) => notify(err.response?.data?.error || 'Failed to cancel', true),
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">My requests <span className="nr-muted" style={{ fontWeight: 400, fontSize: 12 }}>· {total} total{isFetching ? ' · refreshing…' : ''}</span></h2>
        <div className="nr-toggle" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className={`tab ${scope === 'all' ? 'active' : ''}`} onClick={() => setScope('all')}>All</button>
          <button className={`tab ${scope === 'open' ? 'active' : ''}`} onClick={() => setScope('open')}>Open</button>
          <input
            type="text"
            className="form-input form-input-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search order #…"
            style={{ width: 160 }}
          />
        </div>
      </div>

      {error && <div className="message message-error">{error.response?.data?.error || 'Failed to load your requests'}</div>}

      {isLoading ? (
        <SkeletonRows n={3} />
      ) : requests.length === 0 ? (
        <div className="nr-empty">
          {dq.trim() ? `No requests match “${dq.trim()}”.` : scope === 'open' ? 'No open requests — submit one above.' : 'You have not submitted any requests yet.'}
        </div>
      ) : (
        <div className="table-container">
          <table className="nr-table nr-buyer-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Order #</th>
                <th>Note</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Completed</th>
                <th>Listings</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(req => (
                <tr key={req.id}>
                  <td className="nr-mono">{req.id}</td>
                  <td className="nr-mono nr-order">{req.orderNumber}</td>
                  <td>
                    {noteSummary(req)}
                    <RequestPhotos user={user} req={req} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}><When value={req.createdAt} /></td>
                  <td>
                    <StatusChip status={req.status} />
                    <div className="nr-status-text">{buyerStatusText(req, autoApply)}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}><When value={completedAt(req)} emptyText="—" /></td>
                  <td>{(req.listings || []).length ? req.listings.map(secRow).join(' · ') : <span className="nr-muted">—</span>}</td>
                  <td>
                    {OPEN_STATUSES.includes(req.status) && (
                      <button className="btn btn-secondary btn-sm" onClick={() => doCancel(req)} disabled={cancel.isPending}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nr-muted" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', fontSize: 12 }}>
            Showing {requests.length} of {total}
            {requests.length < total && (
              <button className="btn btn-secondary btn-sm" onClick={showMore} disabled={isFetching}>Show more</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Staff body ----------------------------------------------------------------

function whyHere(req, autoApply) {
  if (req.reviewReason && REVIEW_REASON_LABELS[req.reviewReason]) return REVIEW_REASON_LABELS[req.reviewReason];
  if (req.status === 'matched' && !autoApply) return 'pilot — awaiting Apply';
  if (req.status === 'needs_review') return 'needs review';
  return null;
}

function snippet(s, n = 70) {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim();
  return t.length > n ? t.slice(0, n - 1) + '…' : t;
}

function expiresAt(createdAt) {
  const d = parseUtcDate(createdAt);
  return d ? new Date(d.getTime() + WAITING_EXPIRY_DAYS * 86400000) : null;
}

function StaffBody({ user, notify, syncStatus }) {
  const [tab, setTab] = useState('queue');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 300);
  // The All tab is the full history (newest first); it pages by growing the limit.
  const [staffLimit, setStaffLimit] = useState(100);
  const filters = useMemo(() => ({
    tab,
    ...(dq.trim() ? { q: dq.trim() } : {}),
    ...(tab === 'all' ? { limit: staffLimit } : {}),
  }), [tab, dq, staffLimit]);
  const { data, isLoading, error, isFetching } = useNoteRequestList(filters);

  const [applyReq, setApplyReq] = useState(null);
  const [linkOpenId, setLinkOpenId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Bulk selection (Queue + Waiting tabs): request ids ticked on the current tab.
  const [selected, setSelected] = useState(() => new Set());
  const [bulk, setBulk] = useState(null); // 'apply' | 'resolve' | 'cancel'

  const cancel = useCancelNoteRequest();
  const resolve = useResolveNoteRequest();
  const unlink = useUnlinkNoteListing();

  const requests = data?.requests || [];
  const counts = data?.counts || {};
  const autoApply = data?.autoApply ?? syncStatus?.autoApply;
  const enabled = data?.enabled ?? syncStatus?.enabled;

  const doCancel = (req) => {
    if (!window.confirm(`Cancel request #${req.id} (${req.orderNumber}) by ${req.submittedByName || 'unknown'}?`)) return;
    cancel.mutate(req.id, {
      onSuccess: () => notify(`Request #${req.id} cancelled`),
      onError: (err) => notify(err.response?.data?.error || 'Failed to cancel', true),
    });
  };
  const doResolve = (req) => {
    const note = window.prompt(`Resolve request #${req.id} (${req.orderNumber}) without writing anything — optional note for the record:`, '');
    if (note === null) return;
    resolve.mutate({ id: req.id, note: note.trim() || undefined }, {
      onSuccess: () => notify(`Request #${req.id} resolved`),
      onError: (err) => notify(err.response?.data?.error || 'Failed to resolve', true),
    });
  };
  const doUnlink = (req, l) => {
    if (!window.confirm(`Unlink ${secRow(l)} (#${l.inventoryId}) from request #${req.id}?`)) return;
    unlink.mutate({ id: req.id, listingId: l.id }, {
      onSuccess: () => notify(`Unlinked listing #${l.inventoryId}`),
      onError: (err) => notify(err.response?.data?.error || 'Failed to unlink', true),
    });
  };

  const selectableIds = requests.map(r => r.id);
  const selectedVisible = selectableIds.filter(id => selected.has(id));
  const allSelected = selectableIds.length > 0 && selectedVisible.length === selectableIds.length;
  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));
  const clearSelection = () => setSelected(new Set());
  const selectedRequests = requests.filter(r => selected.has(r.id));
  const selectedApplyable = selectedRequests.filter(r => (r.listings || []).some(l => l.state === 'pending' || l.state === 'failed'));

  const tabs = [
    { key: 'queue', label: 'Queue', count: counts.queue },
    { key: 'waiting', label: 'Waiting', count: counts.waiting },
    { key: 'done', label: 'Done', count: counts.done },
    { key: 'all', label: 'All' },
  ];

  const emptyText = {
    queue: 'Queue is empty — nothing needs a pricer right now.',
    waiting: 'No requests are waiting for a listing.',
    done: 'Nothing finished in the last 7 days.',
    all: 'No requests yet.',
  }[tab];

  const listingsCell = (req) => {
    const list = req.listings || [];
    if (!list.length) return <span className="nr-muted">no listings — link one</span>;
    return (
      <div className="nr-listings">
        {list.map(l => (
          <div key={l.id ?? l.inventoryId} className="nr-listing-line">
            <span className="nr-listing-event" title={l.eventName}>{l.eventName || 'Event'}</span>
            <span className="nr-muted"> · {formatEventDateOnly(l.eventDate)} · </span>
            <span>{secRow(l)}{l.seats ? ` · ${l.seats}` : ''}</span>
            <span> · </span>
            <ListingStateChip state={l.state} />
            {l.matchKind ? <span className="nr-muted nr-kind"> {l.matchKind}</span> : null}
            <span className="nr-current-notes" title={l.currentPublicNotes || ''}>
              {' · '}{l.currentPublicNotes ? `“${snippet(l.currentPublicNotes)}”` : <em className="nr-muted">(no public notes)</em>}
            </span>
            {l.state === 'failed' && l.lastError ? <span className="nr-result-bad"> · {l.lastError}{l.attempts ? ` (×${l.attempts})` : ''}</span> : null}
            {l.state === 'applied' || l.state === 'verified' ? <span className="nr-muted"> · {l.appliedSource === 'auto' ? 'auto' : l.appliedByName || 'manual'}{l.appliedAt ? ` ${timeAgo(l.appliedAt)}` : ''}</span> : null}
            {l.eventId ? (
              <a
                className="nr-compare-link"
                href={buildCompareUrl({ eventId: l.eventId, eventName: l.eventName, eventDate: l.eventDate })}
                target="_blank"
                rel="noreferrer"
              >
                Open in Compare
              </a>
            ) : null}
            {(l.state === 'pending' || l.state === 'failed') && (
              <button className="btn btn-link btn-sm nr-unlink" onClick={() => doUnlink(req, l)} disabled={unlink.isPending} title="Remove this listing from the request">
                unlink
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  const orderCell = (req) => {
    const evidence = req.listings?.[0]?.matchedNotes || '';
    return (
      <td className="nr-order-cell" title={evidence ? `Matched on internal notes: ${evidence}` : ''}>
        <div className="nr-mono nr-order">{req.orderNumber}</div>
        {evidence ? <div className="nr-evidence">{snippet(evidence, 48)}</div> : null}
      </td>
    );
  };

  const noteCell = (req) => (
    <td>
      <div>{noteSummary(req)}<HeldChip req={req} /></div>
      {req.comment ? <div className="nr-comment-small" title={req.comment}>“{snippet(req.comment, 80)}”</div> : null}
      <RequestPhotos user={user} req={req} />
    </td>
  );

  const linkRow = (req, colSpan) => linkOpenId === req.id ? (
    <tr key={`${req.id}-link`} className="nr-link-row">
      <td colSpan={colSpan}>
        {/* Stays open after a link so split orders (several listings) can be linked in one go */}
        <NoteRequestLinkPanel request={req} onMessage={notify} />
      </td>
    </tr>
  ) : null;

  return (
    <div className="card">
      <div className="card-header nr-staff-header">
        <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
          {tabs.map(t => (
            <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => { setTab(t.key); setLinkOpenId(null); setExpandedId(null); clearSelection(); setStaffLimit(100); }}>
              {t.label}{t.count != null ? <span className="nr-count-badge">{t.count}</span> : null}
            </button>
          ))}
        </div>
        <div className="nr-search">
          <input
            type="text"
            className="form-input form-input-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search order # or buyer…"
          />
          {isFetching && <span className="nr-muted" style={{ fontSize: 11 }}>refreshing…</span>}
        </div>
      </div>

      {error && <div className="message message-error">{error.response?.data?.error || 'Failed to load requests'}</div>}

      {(tab === 'queue' || tab === 'waiting') && selectedVisible.length > 0 && (
        <div className="nr-bulk-bar">
          <span className="nr-bulk-count">{selectedVisible.length} selected</span>
          {tab === 'queue' && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setBulk('apply')}
              disabled={!selectedApplyable.length}
              title={!selectedApplyable.length ? 'None of the selected requests has a pending listing' : enabled === false ? 'Note sync is off' : 'Write the notes for every selected request'}
            >
              Apply selected{selectedApplyable.length !== selectedVisible.length ? ` (${selectedApplyable.length})` : ''}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setBulk('resolve')} title="Mark handled without writing anything">Resolve selected</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setBulk('cancel')}>Cancel selected</button>
          <button className="btn btn-link btn-sm" onClick={clearSelection}>Clear</button>
        </div>
      )}

      {isLoading ? (
        <SkeletonRows n={4} />
      ) : requests.length === 0 ? (
        <div className="nr-empty">{dq.trim() ? `No requests match “${dq.trim()}”.` : emptyText}</div>
      ) : (
        <div className="table-container">
          <table className="nr-table nr-staff-table">
            {tab === 'queue' && (
              <>
                <thead>
                  <tr>
                    <th className="nr-check-cell"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title="Select all on this tab" /></th>
                    <th>#</th><th>Submitted</th><th>Order #</th><th>Buyer</th><th>Note</th><th>Why here</th><th>Listings</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => {
                    const why = whyHere(req, autoApply);
                    const hasListings = (req.listings || []).length > 0;
                    return (
                      <React.Fragment key={req.id}>
                        <tr className={selected.has(req.id) ? 'nr-row-selected' : ''}>
                          <td className="nr-check-cell"><input type="checkbox" checked={selected.has(req.id)} onChange={() => toggleSelect(req.id)} /></td>
                          <td className="nr-mono">{req.id}</td>
                          <td style={{ whiteSpace: 'nowrap' }}><When value={req.createdAt} /></td>
                          {orderCell(req)}
                          <td>{req.submittedByName || '—'}</td>
                          {noteCell(req)}
                          <td>
                            <StatusChip status={req.status} />
                            {why ? <div><span className="nr-chip nr-why">{why}</span></div> : null}
                          </td>
                          <td className="nr-listings-cell">{listingsCell(req)}</td>
                          <td className="nr-actions">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setApplyReq(req)}
                              disabled={!hasListings}
                              title={!hasListings ? 'Link a listing first' : enabled === false ? 'Note sync is off — preview only' : `Preview before → after and write the note${req.tags?.length ? ' and tags' : ''}`}
                            >
                              Apply
                            </button>
                            <button className={`btn btn-secondary btn-sm ${linkOpenId === req.id ? 'active' : ''}`} onClick={() => setLinkOpenId(linkOpenId === req.id ? null : req.id)}>
                              {linkOpenId === req.id ? 'Close search' : 'Link listing'}
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => doResolve(req)} disabled={resolve.isPending} title="Mark handled without writing anything">Resolve</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => doCancel(req)} disabled={cancel.isPending}>Cancel</button>
                          </td>
                        </tr>
                        {linkRow(req, 9)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </>
            )}

            {tab === 'waiting' && (
              <>
                <thead>
                  <tr>
                    <th className="nr-check-cell"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title="Select all on this tab" /></th>
                    <th>#</th><th>Submitted</th><th>Order #</th><th>Buyer</th><th>Note</th><th>Comment</th><th>Expires</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => {
                    const exp = expiresAt(req.createdAt);
                    return (
                      <React.Fragment key={req.id}>
                        <tr className={selected.has(req.id) ? 'nr-row-selected' : ''}>
                          <td className="nr-check-cell"><input type="checkbox" checked={selected.has(req.id)} onChange={() => toggleSelect(req.id)} /></td>
                          <td className="nr-mono">{req.id}</td>
                          <td style={{ whiteSpace: 'nowrap' }}><When value={req.createdAt} /></td>
                          <td className="nr-order-cell"><div className="nr-mono nr-order">{req.orderNumber}</div><div className="nr-evidence">→ {req.orderKey}</div></td>
                          <td>{req.submittedByName || '—'}</td>
                          <td>{noteSummary(req)}<HeldChip req={req} /></td>
                          <td className="nr-notes-cell">
                            {req.comment ? `“${req.comment}”` : null}
                            <RequestPhotos user={user} req={req} />
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }} title={exp ? formatDateTimeEastern(exp) : ''}>
                            {exp ? formatDateEastern(exp) : '—'}
                            {req.lastCheckedAt ? <div className="nr-muted" style={{ fontSize: 11 }}>checked {timeAgo(req.lastCheckedAt)}</div> : null}
                          </td>
                          <td className="nr-actions">
                            <button className={`btn btn-secondary btn-sm ${linkOpenId === req.id ? 'active' : ''}`} onClick={() => setLinkOpenId(linkOpenId === req.id ? null : req.id)}>
                              {linkOpenId === req.id ? 'Close search' : 'Link listing'}
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => doCancel(req)} disabled={cancel.isPending}>Cancel</button>
                          </td>
                        </tr>
                        {linkRow(req, 9)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </>
            )}

            {(tab === 'done' || tab === 'all') && (
              <>
                <thead>
                  <tr>
                    <th style={{ width: 24 }}></th><th>#</th><th>Order #</th><th>Buyer</th><th>Note</th><th>Status</th><th>Submitted</th><th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => {
                    const open = expandedId === req.id;
                    const list = req.listings || [];
                    return (
                      <React.Fragment key={req.id}>
                        <tr className="nr-expandable" onClick={() => setExpandedId(open ? null : req.id)}>
                          <td className="nr-muted">{open ? '▼' : '▶'}</td>
                          <td className="nr-mono">{req.id}</td>
                          <td className="nr-mono nr-order">{req.orderNumber}</td>
                          <td>{req.submittedByName || '—'}</td>
                          <td>
                            {noteSummary(req)}
                            <HeldChip req={req} />
                            <AttachmentBadge count={(req.attachments || []).length} />
                          </td>
                          <td>
                            <StatusChip status={req.status} />
                            {req.status === 'done' ? <span className="nr-muted" style={{ marginLeft: 6, fontSize: 11 }}>{list.filter(l => l.state === 'applied').length} written · {list.filter(l => l.state === 'verified').length} already had it</span> : null}
                            {req.status === 'resolved' && req.resolvedByName ? <span className="nr-muted" style={{ marginLeft: 6, fontSize: 11 }}>by {req.resolvedByName}</span> : null}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}><When value={req.createdAt} /></td>
                          <td style={{ whiteSpace: 'nowrap' }}><When value={completedAt(req) || req.updatedAt} /></td>
                        </tr>
                        {open && (
                          <tr className="nr-detail-row">
                            <td colSpan={8}>
                              <div className="nr-detail">
                                {req.comment ? <div className="nr-comment-small">Buyer comment: “{req.comment}”</div> : null}
                                <RequestPhotos user={user} req={req} />
                                {(req.resolvedByName || req.resolutionNote) ? (
                                  <div className="nr-muted">
                                    {STATUS_LABELS[req.status] || req.status} by {req.resolvedByName || '—'}{req.resolvedAt ? ` · ${formatDateTimeEastern(req.resolvedAt)}` : ''}
                                    {req.resolutionNote ? ` · “${req.resolutionNote}”` : ''}
                                  </div>
                                ) : null}
                                {list.length === 0 ? (
                                  <div className="nr-muted">No listings were linked.</div>
                                ) : list.map(l => (
                                  <div key={l.id ?? l.inventoryId} className="nr-listing-line">
                                    <span className="nr-listing-event" title={l.eventName}>{l.eventName || 'Event'}</span>
                                    <span className="nr-muted"> · {formatEventDateOnly(l.eventDate)} · </span>
                                    <span>{secRow(l)}{l.seats ? ` · ${l.seats}` : ''}</span>
                                    <span> · </span>
                                    <ListingStateChip state={l.state} />
                                    {(l.state === 'applied' || l.state === 'verified') ? (
                                      <>
                                        <span> · </span>
                                        <NoteDiff before={l.beforeText} after={l.afterText} />
                                        {l.state === 'applied' && req.tags?.length ? <span className="nr-muted"> · tags +{req.tags.join(',')}</span> : null}
                                        <span className="nr-muted"> · {l.appliedSource === 'auto' ? 'auto (sweeper)' : `${l.appliedByName || 'manual'}`}{l.appliedAt ? ` · ${formatDateTimeEastern(l.appliedAt)}` : ''}</span>
                                      </>
                                    ) : l.state === 'failed' && l.lastError ? (
                                      <span className="nr-result-bad"> · {l.lastError}</span>
                                    ) : l.lastError ? (
                                      <span className="nr-muted"> · {l.lastError}</span>
                                    ) : null}
                                    {l.eventId ? (
                                      <a className="nr-compare-link" href={buildCompareUrl({ eventId: l.eventId, eventName: l.eventName, eventDate: l.eventDate })} target="_blank" rel="noreferrer">Open in Compare</a>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>
          {tab === 'all' && (
            <div className="nr-muted" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', fontSize: 12 }}>
              Showing {requests.length}{requests.length >= staffLimit ? '+' : ''} (newest first)
              {requests.length >= staffLimit && (
                <button className="btn btn-secondary btn-sm" onClick={() => setStaffLimit(l => Math.min(2000, l + 100))} disabled={isFetching}>Show more</button>
              )}
            </div>
          )}
        </div>
      )}

      {applyReq && (
        <NoteRequestApplyModal
          request={applyReq}
          enabled={enabled}
          onClose={() => setApplyReq(null)}
          onMessage={notify}
        />
      )}
      {bulk && (
        <NoteRequestBulkModal
          action={bulk}
          requests={selectedRequests}
          enabled={enabled}
          onClose={() => { setBulk(null); clearSelection(); }}
          onMessage={notify}
        />
      )}
    </div>
  );
}

function SkeletonRows({ n = 3 }) {
  return (
    <div className="nr-skeleton">
      {Array.from({ length: n }).map((_, i) => <div key={i} className="skeleton nr-skel-row" />)}
    </div>
  );
}

export default NoteRequests;
